import { useState, useEffect, useRef, useMemo } from "react";
import { NavLink } from "react-router-dom";
import { Modal, ModalContent, ModalBody, Input, Kbd } from "@heroui/react";
import { LuSearch } from "react-icons/lu";
import { useUser } from "@/context/UserContext";
import { ThemedText } from "@/components/ui";

/**
 * Flatten all routes into searchable items with permission filtering.
 */
const flattenRoutes = (routes, hasPermission) => {
    const items = [];

    const traverse = (route, sectionTitle = "", parentTitle = "") => {
        // Section with items
        if (route.items) {
            route.items.forEach((item) => traverse(item, route.title, ""));
            return;
        }

        // Has children
        if (route.children) {
            const visibleChildren = route.children.filter(
                (child) =>
                    child.type !== "hidden" &&
                    (!child.permission || hasPermission(child.permission)),
            );

            if (visibleChildren.length === 1) {
                // Single visible child — use parent title, skip child
                const child = visibleChildren[0];
                items.push({
                    path: child.path,
                    title: route.title,
                    section: sectionTitle,
                    icon: route.icon,
                });
            } else {
                // Multiple visible children — traverse each
                visibleChildren.forEach((child) =>
                    traverse(child, sectionTitle, route.title),
                );
            }
            return;
        }

        // Leaf route — check permission and not hidden
        if (route.path && route.component) {
            if (route.permission && !hasPermission(route.permission)) return;
            if (route.type === "hidden") return;

            items.push({
                path: route.path,
                title: route.title,
                section: parentTitle || sectionTitle,
                icon: route.icon,
            });
        }
    };

    if (Array.isArray(routes)) {
        routes.forEach((r) => traverse(r));
    } else {
        Object.values(routes).forEach((r) => traverse(r));
    }

    return items;
};

const SearchModal = ({ isOpen, onClose, menuItems }) => {
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const inputRef = useRef(null);
    const { hasPermission } = useUser();

    const allItems = useMemo(
        () => flattenRoutes(menuItems, hasPermission),
        [menuItems, hasPermission],
    );

    const results = useMemo(() => {
        if (!query.trim()) return [];
        const q = query.toLowerCase();
        return allItems.filter(
            (item) =>
                item.title.toLowerCase().includes(q) ||
                item.section.toLowerCase().includes(q),
        );
    }, [query, allItems]);

    useEffect(() => {
        if (isOpen) {
            setQuery("");
            setSelectedIndex(0);
        }
    }, [isOpen]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [results]);

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            e.preventDefault();
            onClose();
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setSelectedIndex((prev) =>
                prev < results.length - 1 ? prev + 1 : 0,
            );
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setSelectedIndex((prev) =>
                prev > 0 ? prev - 1 : results.length - 1,
            );
        } else if (e.key === "Enter" && results[selectedIndex]) {
            navigate(results[selectedIndex].path);
            onClose();
        }
    };

    const handleSelect = () => {
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="lg"
            placement="center"
            hideCloseButton
        >
            <ModalContent>
                <ModalBody className="p-0">
                    <div className="p-3">
                        <Input
                            ref={inputRef}
                            autoFocus
                            placeholder="Cari menu..."
                            value={query}
                            onValueChange={setQuery}
                            onKeyDown={handleKeyDown}
                            startContent={
                                <LuSearch
                                    size={18}
                                    className="text-default-400"
                                />
                            }
                            endContent={<Kbd>ESC</Kbd>}
                            variant="flat"
                            size="lg"
                            classNames={{
                                input: "text-sm",
                            }}
                        />
                    </div>

                    <div className="max-h-80 overflow-y-auto px-3 pb-3">
                        {!query.trim() ? (
                            <div className="py-8 text-center">
                                <ThemedText variant="secondary" size="sm">
                                    Ketik nama menu untuk mencari halaman
                                </ThemedText>
                            </div>
                        ) : results.length === 0 ? (
                            <div className="py-8 text-center">
                                <ThemedText variant="secondary" size="sm">
                                    Pencarian tidak ditemukan
                                </ThemedText>
                            </div>
                        ) : (
                            <div className="flex flex-col gap-0.5">
                                {results.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        onClick={handleSelect}
                                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-default-100"
                                    >
                                        {item.icon && (
                                            <span className="text-default-500 shrink-0 text-lg">
                                                {item.icon}
                                            </span>
                                        )}
                                        <div className="min-w-0 flex-1">
                                            <ThemedText
                                                size="sm"
                                                className="font-medium"
                                            >
                                                {item.title}
                                            </ThemedText>
                                            <ThemedText
                                                size="xs"
                                                variant="secondary"
                                            >
                                                {item.section}
                                            </ThemedText>
                                        </div>
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SearchModal;
